import { convertValues } from '~/util';

import { client } from './client/Client';

const convertCardTokenResponse = (response: any): CardToken => {
  const token = convertValues<CardToken>(response, undefined, {
    cardId: response.cardId || response.card_id,
    cardAccountId: response.cardAccountId || response.card_account_id,
    cardProgramId: response.cardProgramId || response.card_program_id,
    walletType: response.walletType || response.wallet_type,
    tokenType: response.tokenType || response.token_type,
    deviceType: response.deviceType || response.device_type,
    deviceName: response.deviceName || response.device_name,
    requestorName: response.requestorName || response.requestor_name,
    pendingActivationAt: response.pendingActivationAt || response.pending_activation_at,
    steppingUpAt: response.steppingUpAt || response.stepping_up_at,
    pendingAuthenticationAt: response.pendingAuthenticationAt || response.pending_authentication_at,
    activationDeniedAt: response.activationDeniedAt || response.activation_denied_at,
    activatedAt: response.activatedAt || response.activated_at,
    suspendedAt: response.suspendedAt || response.suspended_at,
    deactivatedAt: response.deactivatedAt || response.deactivated_at,
  });
  const rawDate = response.createdAt || response.created_at;
  if (rawDate) {
    token.createdAt = new Date(rawDate);
  }
  return token;
};

export interface CardToken {
  id: string;
  cardId: string;
  cardAccountId: string;
  cardProgramId: string;
  status: string;
  walletType: string;
  tokenType?: string;
  deviceType?: string;
  deviceName?: string;
  requestorName?: string;
  createdAt: Date;
  pendingActivationAt?: string;
  steppingUpAt?: string;
  pendingAuthenticationAt?: string;
  activationDeniedAt?: string;
  activatedAt?: string;
  suspendedAt?: string;
  deactivatedAt?: string;
}

interface ListCardTokensResponse {
  cardTokens: CardToken[];
  hasMore: boolean;
}

export interface ListCardTokensParams {
  cardId?: string;
  limit?: number;
  startingAfter?: string;
  endingBefore?: string;
}

export class CardTokenRepository {
  static async get(id: string): Promise<CardToken> {
    return client.get<unknown, CardToken>(`/issuing/card-tokens/${id}`).then((response) => {
      return convertCardTokenResponse(response);
    });
  }

  static async activate(id: string): Promise<CardToken> {
    return client.post<unknown, CardToken>(`/issuing/card-tokens/${id}/activate`, {}).then((response) => {
      return convertCardTokenResponse(response);
    });
  }

  static async suspend(id: string): Promise<CardToken> {
    return client.post<unknown, CardToken>(`/issuing/card-tokens/${id}/pause`, {}).then((response) => {
      return convertCardTokenResponse(response);
    });
  }

  static async deactivate(id: string): Promise<CardToken> {
    return client.post<unknown, CardToken>(`/issuing/card-tokens/${id}/deactivate`, {}).then((response) => {
      return convertCardTokenResponse(response);
    });
  }

  static async listCardTokens(query?: ListCardTokensParams): Promise<ListCardTokensResponse> {
    try {
      return await client
        .get<ListCardTokensParams, ListCardTokensResponse>('/issuing/card-tokens', { query })
        .then((response) => ({
          cardTokens: Array.isArray(response?.cardTokens)
            ? response.cardTokens.map((e) => convertCardTokenResponse(e))
            : [],
          hasMore: Boolean(response?.hasMore),
        }));
    } catch (error) {
      console.error('Failed to fetch card tokens:', error);
      return { cardTokens: [], hasMore: false };
    }
  }
}
